import { format, formatDistanceToNowStrict, isBefore, isSameDay, isThisYear, isToday } from 'date-fns';
import russianLocale from 'date-fns/locale/ru';
import getConfig from 'next/config';

import { sentryHandler } from '@sravni/ugc/lib/utils/sentry/sentryHandler';

import { GetBannersParams } from '../__generated__/gateway/Api';
import {
  SravniNewsSharedEnumsMaterialType,
  SravniNewsSharedModelsAuthorsAuthorReadModel,
  SravniNewsSharedModelsMaterialsHighLight,
} from '../__generated__/news/Api';
import { getDateForUrl } from '../components/MaterialContent/helpers/useInfiniteScroll';
import { REDUX_STORE, TRIGGER_WORDS } from '../constants/common';
import { URL_TYPES } from '../constants/news';
import { MAP_TYPE_TO_URL } from '../constants/organizations';
import {
  AMP_MATERIAL,
  AMP_SINGLE_NEWS,
  LIST_PARTNERS,
  LIST_PARTNERS_WITH_TYPE,
  MATERIAL,
  MFO_LIST_PARTNERS_WITH_TYPE,
  NEWS,
} from '../constants/routes';
import { TAdMaterialTagsList } from '../interfaces/advertising';
import { IMaterial, TMaterialHighlightsList, TMaterialList } from '../interfaces/news';
import { toHighlightsFormat } from '../server/services/news/utils';
import { ISortedTagsList } from '../types/tags';
import { isServer } from '../utils/isServer';
import { createLinkProps } from '../utils/routing';

import { moscowDateToUTCDate } from './moscowDateToUTCDate';

export const getLowerCaseName = (name: string) => {
  return name.charAt(0).toLowerCase() + name?.slice(1);
};

interface MaterialProductParams {
  productTags: string[];
  adTags: TAdMaterialTagsList;
}

export const getMaterialProduct = ({ productTags, adTags }: MaterialProductParams): GetBannersParams['Product'] => {
  const tagForService = productTags?.find((tag) => adTags[tag]);

  return (adTags[tagForService]?.code || 'none') as GetBannersParams['Product'];
};

export const getMaterialLink = (
  material: Pick<
    IMaterial & SravniNewsSharedModelsMaterialsHighLight,
    'type' | 'publishDate' | 'alias' | 'pubDate' | 'rubric'
  >,
  queryParams?: Record<string, any>,
  isAmp?: boolean,
) => {
  const { type, publishDate, pubDate, alias } = material;

  const date = pubDate || publishDate;
  const isNews = type === SravniNewsSharedEnumsMaterialType.News;
  const pattern = isNews ? (isAmp ? AMP_SINGLE_NEWS : NEWS) : isAmp ? AMP_MATERIAL : MATERIAL;
  const { year, month, day } = getDateForUrl(date);

  let typeByRubric = URL_TYPES[type] || URL_TYPES[SravniNewsSharedEnumsMaterialType.Article];
  if (type === SravniNewsSharedEnumsMaterialType.News && material.rubric === 'novosti-sporta') {
    typeByRubric = 'novost-sporta';
  }

  let link;

  try {
    link = createLinkProps('material', pattern, {
      type: typeByRubric,
      alias,
      year,
      month,
      day,
      queryParams,
    });
  } catch (e) {
    if (!isServer) {
      const materials = window[REDUX_STORE].getState().news.material.data.map(toHighlightsFormat);
      sentryHandler(e, { tag: 'create-link-error', extra: JSON.stringify(materials) });
    }
    link = createLinkProps('material', pattern, {
      type: typeByRubric,
      alias,
      year,
      month,
      day,
      queryParams,
    });
  }

  return link;
};

export const getDate = (date: string) => {
  const dateFormat = new Date(date);

  if (isToday(dateFormat)) {
    return formatDistanceToNowStrict(dateFormat, { addSuffix: true, locale: russianLocale });
  }

  if (isThisYear(dateFormat)) {
    return format(dateFormat, 'd MMMM', { locale: russianLocale });
  }

  return format(dateFormat, 'd MMMM yyyy', { locale: russianLocale });
};

export const getUpdateDate = (pubDate: string, updateDate: string) => {
  if (isSameDay(new Date(pubDate), new Date(updateDate))) return null;
  if (isBefore(new Date(updateDate), new Date(pubDate))) return null;

  return getDate(updateDate);
};

export const sliceOrganizationName = (name: string): string => {
  let res = name;

  if (name.length > 17) {
    const nameArr = name.split(' ');
    let separator = nameArr.length / 2;

    if (nameArr.length % 2) {
      separator = (nameArr.length - 1) / 2;
    }

    const firstString = nameArr.slice(0, separator).join(' ');
    const secondString = nameArr.slice(separator, nameArr.length).join(' ');

    res = firstString + '\n' + secondString;
  }

  return res;
};

export const checkTriggerWords = (text: string): boolean => {
  if (text) {
    return TRIGGER_WORDS.some((word) => text.toLowerCase().includes(word.toLowerCase()));
  }
  return false;
};

export const decorateAuthor = (item: SravniNewsSharedModelsAuthorsAuthorReadModel) => {
  const clearItem = { ...item };
  return clearItem;
};

export const decorateMaterial = (item: IMaterial) => {
  return {
    ...item,
    authors: item.authors?.map((author) => decorateAuthor(author)),
  };
};

export const decorateMaterialsList = (list: TMaterialList) => {
  return {
    ...list,
    items: list.items?.map((item) => decorateMaterial(item)),
  };
};

export const getAuthorAvatarUrl = (authorId: number | string) => {
  return `${getConfig().publicRuntimeConfig.avatarURLPrefix}/${authorId}-xs.svg`;
};

export const sortTagsList = (list, activeTags): ISortedTagsList => {
  if (!activeTags) return list;

  const orderedSource = list.reduce(
    (output: { selected; unselected }, item) => {
      if (activeTags.includes(item.label)) {
        output.selected[item.label] = item;
      } else {
        output.unselected.push(item);
      }

      return output;
    },
    { selected: {}, unselected: [] },
  );

  const selected = activeTags.reduce((source, i) => {
    if (orderedSource.selected[i]) {
      source.push(orderedSource.selected[i]);
    }

    return source;
  }, []);

  return selected.concat(orderedSource.unselected);
};

export const decorateMaterialHighlight = (item: SravniNewsSharedModelsMaterialsHighLight) => {
  return {
    ...item,
    pubDate: moscowDateToUTCDate(item.pubDate),
  };
};

export const decorateMaterialHighlightsList = (data: TMaterialHighlightsList) => {
  return {
    ...data,
    items: data.items?.map(decorateMaterialHighlight),
  };
};

export const decorateMaterialWithDate = (material: IMaterial) => ({
  ...material,
  createDate: moscowDateToUTCDate(material.createDate),
  lastChangeDate: moscowDateToUTCDate(material.lastChangeDate),
  publishDate: moscowDateToUTCDate(material.publishDate),
});

export const getPartnerNewsLink = (type: string, alias: string) => {
  let pattern = LIST_PARTNERS_WITH_TYPE;

  if (type === 'other') {
    pattern = LIST_PARTNERS;
  } else if (type === 'mfo') {
    pattern = MFO_LIST_PARTNERS_WITH_TYPE;
  }

  const typeForLink = MAP_TYPE_TO_URL[type];

  return createLinkProps('list', pattern, {
    ...(typeForLink && { organizationType: typeForLink }),
    organizationAlias: alias,
  });
};
